import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { BOOKING_CALENDAR_LICENSE_KEY } from "src/app.config";
import { useAppMode } from "src/contexts/app-mode";
import { selectAllCalendarEvents } from "src/selectors/bookingCalendar.selectors";
import { useWindowSize } from "usehooks-ts";

import Loader from "@components/shared/Loader/Loader";
import {
  KIOSK_HEIGHT,
  SHOW_HIDDEN_BOOKING_TYPES,
  TIME_CONSTANTS,
} from "@constants/Bookings";
import { DateFormats } from "@enums/shared.enum";
import { EventClickArg } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import enLocale from "@fullcalendar/core/locales/en-gb";
import frLocale from "@fullcalendar/core/locales/fr";
import itLocale from "@fullcalendar/core/locales/it";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import scrollgridPlugin from "@fullcalendar/scrollgrid";
import { useBookingCalendar } from "@hooks/UseBookingCalendar/UseBookingCalendar";
import { useBookingCalendarEvents } from "@hooks/UseBookingCalendar/UseBookingCalendarEvents";
import { useDrawerLimits } from "@hooks/useDrawerLimits/useDrawerLimits";
import { useKioskAuth } from "@hooks/useKioskAuth";
import { useUserProfile } from "@hooks/useUserProfile/useUserProfile";
import {
  BookingCalendarProps,
  ParticipantType,
} from "@models/components/booking.model";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";

import BookingParticipantModal from "../BookingParticipantModal/BookingParticipantModal";

const BookingCalendar: FC<BookingCalendarProps> = ({
  bookingLoader,
  locationId,
  refreshResources,
  slots,
}) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [participant, setParticipant] = useState<ParticipantType | null>(null);

  const [, setHasError] = useState(false);
  const hasScrolledRef = useRef<boolean>(false);

  const { isAuthenticated, requireAuth } = useKioskAuth();

  const { maxHeight } = useDrawerLimits();

  const {
    calendarTime,
    fetchCalendarData,
    fetchCalendarParticipant,
    isLoading,
    renderEventContent,
    resources,
    setIsLoading,
  } = useBookingCalendar(slots);

  const {
    eventClickOrg,
    getEventClassName,
    handleDateClick,
    handleEventDrop,
    handleEventResize,
    handleTimeSelect,
    setEventClickOrg,
  } = useBookingCalendarEvents(slots, setHasError, setIsLoading);
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const selectedLanguage = useSelector(
    (state: RootState) => state.user.selectedLanguage
  );

  const selectedDate = useSelector(
    (state: RootState) => state.booking.selectedDate
  );

  const { isKioskMode } = useAppMode();

  const dispatch = useDispatch();

  const calendarEvents = useSelector(selectAllCalendarEvents);

  const accessToken = useSelector(
    (state: RootState) => state.local.accessToken
  );
  const { data: userProfile } = useUserProfile(accessToken, {
    enabled: isAuthenticated && !!accessToken,
  });
  const currentUserId = userProfile?.profile?.id;
  const [participantModal, setParticipantModal] = useState(false);
  const [subType, setSubType] = useState<string | undefined>(undefined);
  const toggleParticipantModal = () => {
    setParticipantModal(!participantModal);
  };

  const [stableResources, setStableResources] =
    useState<typeof resources>(resources);
  const [events, setEvents] = useState<typeof calendarEvents>(
    () => calendarEvents
  );
  useEffect(() => {
    if (isLoading) {
      return;
    }
    setStableResources(resources);
  }, [isLoading, resources]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setEvents(calendarEvents);
  }, [isLoading, calendarEvents]);
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [selectedDate, locationId, selectedLanguage]);

  useEffect(() => {
    setHasError(false);
    fetchCalendarData({ locationId });
  }, [selectedLanguage, selectedDate, locationId, slots?.resources]);

  const initialScrollTime = useMemo(() => {
    if (
      moment(selectedDate, DateFormats.YMD).isSame(moment(), DateFormats.Day)
    ) {
      return moment()
        .subtract(1, DateFormats.Hour)
        .format(DateFormats.TimeWithSeconds);
    }
    return calendarTime.start || TIME_CONSTANTS.START_OF_DAY;
  }, [selectedDate, calendarTime.start]);

  const handleEventClick = async (clickInfo: EventClickArg) => {
    if (!isAuthenticated) {
      setEventClickOrg(clickInfo);
    }
    const { bookingId, showDrawer, showPopup, subType } =
      clickInfo.event.extendedProps;

    if (subType && SHOW_HIDDEN_BOOKING_TYPES.includes(subType)) {
      return;
    }

    setSubType(subType);
    if (showDrawer) {
      if (!requireAuth()) {
        return;
      }
      dispatch(drawerSlice.actions.setOpenPercent(maxHeight));
      return;
    }

    if (!showPopup && !isAuthenticated) {
      requireAuth();
      return;
    }

    const participantObj = await fetchCalendarParticipant(bookingId);

    if (!showPopup && currentUserId !== participantObj?.organizer?.[0]?.id) {
      return;
    } else {
      setParticipant(participantObj);
      toggleParticipantModal();
    }
  };

  useEffect(() => {
    if (isAuthenticated && eventClickOrg && currentUserId) {
      handleEventClick(eventClickOrg);
      setEventClickOrg(null);
    }
  }, [isAuthenticated, eventClickOrg, currentUserId]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (calendarWrapperRef) {
        calendarWrapperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, [calendarWrapperRef, isKioskMode]);

  useEffect(() => {
    if (isLoading || hasScrolledRef.current) return;

    const timer = setTimeout(() => {
      if (isKioskMode) {
        const calendarApi = calendarRef.current?.getApi();

        if (calendarApi) {
          calendarApi.scrollToTime(initialScrollTime);

          hasScrolledRef.current = true;
        }
      } else {
        const wrapper = calendarWrapperRef.current;

        if (!wrapper) return;

        const formattedTime = moment(initialScrollTime, "HH:mm:ss")
          .startOf("hour")
          .format("HH:mm:ss");

        const slot = wrapper.querySelector(
          `[data-time="${formattedTime}"]`
        ) as HTMLElement | null;

        if (slot) {
          slot.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          hasScrolledRef.current = true;
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isLoading, initialScrollTime, isKioskMode]);
  const { height: windowHeight } = useWindowSize();

  const {
    CALENDAR_HEADER_HEIGHT,
    CONTAINER_MARGINS,
    FOOTER_HEIGHT,
    NAVBAR_HEIGHT,
    NON_KIOSK_MAX_HEIGHT,
  } = KIOSK_HEIGHT;

  const kioskCalendarHeight =
    (windowHeight || 0) -
    NAVBAR_HEIGHT -
    FOOTER_HEIGHT -
    CALENDAR_HEADER_HEIGHT -
    CONTAINER_MARGINS;

  const calendarHeight = isKioskMode
    ? Math.max(kioskCalendarHeight, 300)
    : "auto";

  return (
    <>
      <div
        className="fg-dev-booking-calendar mb-3 position-relative"
        ref={calendarWrapperRef}
        style={
          !isKioskMode
            ? { maxHeight: NON_KIOSK_MAX_HEIGHT, overflowY: "auto" }
            : undefined
        }
      >
        <Loader isLoading={isLoading && !bookingLoader} />
        <FullCalendar
          plugins={useMemo(
            () => [resourceTimeGridPlugin, interactionPlugin, scrollgridPlugin],
            []
          )}
          headerToolbar={useMemo(
            () => ({
              center: "",
              left: "",
              right: "",
            }),
            []
          )}
          schedulerLicenseKey={BOOKING_CALENDAR_LICENSE_KEY}
          key={`${selectedDate}-${calendarTime.start}-${calendarTime.end}`}
          initialDate={selectedDate}
          scrollTimeReset={false}
          eventClassNames={getEventClassName}
          ref={calendarRef}
          aspectRatio={1.5}
          locale={selectedLanguage}
          locales={useMemo(() => [enLocale, deLocale, itLocale, frLocale], [])}
          themeSystem="bootstrap5"
          eventOverlap={false}
          selectOverlap={false}
          timeZone="local"
          allDaySlot={false}
          scrollTime={initialScrollTime}
          slotDuration={TIME_CONSTANTS.START_OF_DAY_15_MINUTE_MARK}
          snapDuration={TIME_CONSTANTS.START_OF_DAY_15_MINUTE_MARK}
          slotMinTime={calendarTime.start || TIME_CONSTANTS.START_OF_DAY}
          slotMaxTime={calendarTime.end || TIME_CONSTANTS.END_OF_DAY}
          slotLabelFormat={useMemo(
            () => ({
              hour: "2-digit",
              hour12: false,
              minute: "2-digit",
            }),
            []
          )}
          nowIndicator={true}
          initialView="resourceTimeGridDay"
          height={calendarHeight}
          resources={stableResources}
          resourceOrder="sortOrder"
          editable={false}
          selectable={true}
          selectMirror={true}
          unselectAuto={true}
          longPressDelay={200}
          selectLongPressDelay={200}
          eventResizableFromStart={true}
          dayMaxEvents={true}
          weekends={true}
          events={events}
          dateClick={handleDateClick}
          select={handleTimeSelect}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          dayMinWidth={200}
          eventResize={handleEventResize}
          eventDrop={handleEventDrop}
        />
      </div>

      <BookingParticipantModal
        participantModal={participantModal}
        toggleParticipantModal={toggleParticipantModal}
        participant={participant}
        setParticipant={participant => setParticipant(participant ?? null)}
        isLoading={isLoading}
        subType={subType}
        refreshCalendar={() => fetchCalendarData({ locationId })}
        refreshResources={refreshResources}
      />
    </>
  );
};

export default memo(BookingCalendar);
